import { Menu } from "@etudescarre/ui";
import { isNil, isNotNil } from "@etudescarre/utils";
import {
  KeyIcon,
  SignOutIcon,
  UserIcon,
  UserSwitchIcon,
} from "@phosphor-icons/react";
import { useEffect, useState } from "react";
import { useMatch, useNavigate } from "react-router";
import { toaster } from "@/app";
import { apiClient } from "@/lib/api/client";
import { authClient } from "@/lib/auth-client";
import {
  confirmLeaveWithUnsavedChanges,
  useGuardedNavigate,
} from "@/lib/utils/unsaved-changes";
import { type AnticipatedYard, yardAnticipations } from "@/lib/yard-access";

const displayName = (firstName?: string | null, lastName?: string | null) =>
  [firstName, lastName?.toUpperCase()].filter(Boolean).join(" ");

export const Header = () => {
  const navigate = useNavigate();
  const guardedNavigate = useGuardedNavigate();
  const session = authClient.useSession();
  const currentYardId = useMatch("/yards/:yardId/*")?.params.yardId;
  const [stoppingImpersonation, setStoppingImpersonation] = useState(false);
  const [impersonation, setImpersonation] = useState<{
    write: boolean;
    impersonatorEmail: string | null;
    impersonatorFirstName: string | null;
    impersonatorLastName: string | null;
    passwordCreated: boolean;
    anticipatedYards: AnticipatedYard[];
  } | null>(null);

  const isImpersonating = Boolean(session.data?.session.impersonatedBy);
  const user = session.data?.user;
  const accountName = isImpersonating
    ? displayName(
        impersonation?.impersonatorFirstName,
        impersonation?.impersonatorLastName,
      )
    : displayName(user?.firstName, user?.lastName);
  const accountLogin = isImpersonating
    ? impersonation?.impersonatorEmail
    : user?.email;

  useEffect(() => {
    if (!isImpersonating) {
      setImpersonation(null);
      return;
    }
    apiClient.GET("/api/impersonation/mode").then(({ data }) => {
      if (data) setImpersonation(data.data);
    });
  }, [isImpersonating]);

  const handleLogout = () => {
    guardedNavigate("/auth/sign-out");
  };

  const handleStopImpersonating = async () => {
    if (!(await confirmLeaveWithUnsavedChanges())) return;
    setStoppingImpersonation(true);
    try {
      // The staff guard must see the restored account before /staff renders,
      // or it refuses the page. Without disableSignal, better-auth refetches
      // the session 10 ms later and that fetch aborts the awaited one below.
      const { error } = await authClient.admin.stopImpersonating({
        fetchOptions: { disableSignal: true },
      });
      if (error) {
        toaster.error({
          title: "Erreur",
          description: "Impossible de revenir à votre compte",
        });
        return;
      }
      await session.refetch();
      navigate("/staff");
    } finally {
      setStoppingImpersonation(false);
    }
  };

  // What the user could not do yet on their own, anticipated by staff and
  // stated in the banner.
  const anticipations = isNil(impersonation)
    ? []
    : yardAnticipations(impersonation.anticipatedYards, currentYardId);
  if (isNotNil(impersonation) && !impersonation.passwordCreated) {
    anticipations.unshift(
      "Anticipation : ce compte n'a pas encore de mot de passe et ne peut pas encore se connecter lui-même.",
    );
  }

  return (
    <div className="sticky top-0 z-40 border-gray-200 border-b bg-white">
      {isImpersonating && session.data?.user && (
        <div className="bg-amber-100 px-4 py-1.5 text-amber-900 text-sm">
          <div className="flex items-center justify-center gap-3">
            <UserSwitchIcon className="h-4 w-4" />
            <span>
              Vous incarnez{" "}
              <strong>
                {session.data.user.name || session.data.user.email}
              </strong>
              {isNotNil(impersonation) &&
                (impersonation.write ? " (avec écriture)" : " (lecture seule)")}
            </span>
            <button
              type="button"
              onClick={handleStopImpersonating}
              disabled={stoppingImpersonation}
              className="cursor-pointer font-medium underline hover:text-amber-700 disabled:opacity-50"
            >
              {stoppingImpersonation ? "Retour..." : "Revenir à mon compte"}
            </button>
          </div>
          {anticipations.map((text) => (
            <p key={text} className="text-center text-xs">
              {text}
            </p>
          ))}
        </div>
      )}
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex h-16 items-center justify-between">
          <button
            type="button"
            className="shrink-0 cursor-pointer hover:opacity-80 transition-opacity"
            onClick={() => guardedNavigate("/")}
          >
            <img src="/logo.png" alt="ChantierCarré" className="h-10" />
          </button>
          <div className="flex min-w-0 items-center gap-4">
            {user && (
              <Menu positioning={{ placement: "bottom-end" }}>
                <Menu.Trigger
                  plain
                  className="!items-center max-w-80"
                  title={[accountName, accountLogin]
                    .filter(Boolean)
                    .join(" / ")}
                >
                  <UserIcon className="h-4 w-4" data-slot="icon" />
                  <span className="flex min-w-0 flex-col text-left">
                    {accountName && (
                      <span className="truncate leading-5">{accountName}</span>
                    )}
                    <span
                      className={
                        accountName
                          ? "truncate font-normal text-xs text-zinc-500"
                          : "truncate"
                      }
                    >
                      {accountLogin}
                    </span>
                  </span>
                </Menu.Trigger>
                <Menu.Content>
                  {!isImpersonating && (
                    <Menu.Item
                      value="change-password"
                      onClick={() => {
                        guardedNavigate("/auth/change-password");
                      }}
                    >
                      <KeyIcon className="h-4 w-4" />
                      Modifier le mot de passe
                    </Menu.Item>
                  )}
                  <Menu.Item value="logout" onClick={handleLogout}>
                    <SignOutIcon className="h-4 w-4" />
                    Déconnexion
                  </Menu.Item>
                </Menu.Content>
              </Menu>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
